import * as React from 'react';
import * as _ from 'underscore';

import { BodyCell, DataGrid } from '@tableau/data-grid-3-wrapper';

import { Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { CellWithCount } from '../../../common/react/components/dataGrid/CellWithCount';
import { IReactTranslateService } from '../../../common/react/IReactTranslateService';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { L10n } from '../../../ts/L10n/L10n';
import { DataAlertRecipientInfo, DataAlertRecipientInfoType, IDataAlertInfo, UserDataAlertRecipientInfo } from '../../../ts/models/IDataAlertInfo';
import { HelpService } from '../../../ts/services/HelpService';
import { ServerService } from '../../../ts/services/ServerService';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { DataGridCells } from './DataGridCells';
import { StyledCellFormatter } from './StyledCellWrapper';

export namespace DataAlertDataGridCells {
  export function LastAlertCell(nullDateTimeLabel: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function LastAlertCell(props: BodyCell.Props) {
        const dataAlert = props.row.data as IDataAlertInfo;
        const lastAlerted: Date =  dataAlert.lastSentForCurrentUser;

        const text = lastAlerted ? L10n.localizedDateTime(lastAlerted) : nullDateTimeLabel;
        return <span data-tb-test-id={ 'last-alert-value' }>{ text }</span>;
      }
    );
  }

  export function buildLastCheckedFormatter(translate: IReactTranslateService, helpService: HelpService) {
    if (FeatureFlags.suspendDataAlerts) {
      return StyledCellFormatter(DataGridCells.InteractiveIconTooltipCell((row: DataGrid.Row) => {
        const alertInfo = row.data as IDataAlertInfo;
        const dateTime = row.data.lastChecked;
        const dateAsText = dateTime ? L10n.localizedDateTime(dateTime) : Messages.never();

        return {
          text: dateAsText,
          title: Messages.learnMore(),
          iconClass:(alertInfo.suspendState !== 'NotSuspended') ? 'tb-react-dg-hcol tb-icon-server-alert-list-suspended' : null,
          tooltipText:'suspendedAlertTooltipText',
          tooltipLinkText: Messages.learnMore(),
          tooltipLink: helpService.getServerHelpUrlByCategory('alert-suspend')
        };
      }, translate));
    } else {
      return StyledCellFormatter(DataGridCells.DateTimeCell('lastChecked', Messages.never()));
    }
  }

  export function createMapNotificationInterval(){
    return function(notificationIntervalInMinutes: number){

      // let's be careful and check it isn't some invalid number
      if (notificationIntervalInMinutes < 0){
        return Messages.unspecified();
      }
      const MAXINT = 2147483647;
      const HOUR = 60;
      switch (notificationIntervalInMinutes){
        case (0):
          return Messages.frequencyAlways();
        case (HOUR):
          return Messages.frequencyHourly();
        case(HOUR * 24):
          return Messages.frequencyDaily();
        case(HOUR * 24 * 7):
          return Messages.frequencyWeekly();
        case(MAXINT):
          return Messages.frequencyOnce();
        default:
          const localizedCount = L10n.localizedCount(notificationIntervalInMinutes);
          return Messages.frequencyXMinutes({COUNT_LOC: localizedCount});
      }
    };
  }

  export interface RecipientsListCellParams {
    numRecipientsShown: number;
    siteUrls: SpacesSiteUrls;
    server: ServerService;
    onPillClick: (dataAlertInfo: IDataAlertInfo) => void;
    testId?: string;
  }
  export function RecipientsListCell(params: RecipientsListCellParams): React.Factory<BodyCell.Props> {
    return StyledCellFormatter(
      React.createFactory(class extends React.Component<BodyCell.Props, any> {
        private onClickFn = () => params.onPillClick(this.props.row.data as IDataAlertInfo);

        private textStyles = _.extend({ display: 'block' }, CommonStyles.Layout.TextEllipsis);

        public render() {
          const dataAlert = this.props.row.data as IDataAlertInfo;
          let recipients: DataAlertRecipientInfo[] = dataAlert.displayRecipients;

          if (!recipients || recipients.length === 0) {
            const noneText = Messages.none();
            return <span>{ noneText }</span>;
          }

          const currentUser: UserDataAlertRecipientInfo = {
            type: 'user',
            user:  params.server.sessionUserInfo()
          };

          if (dataAlert.isCurrentUserSubscribed && !(isCurrentUser(dataAlert.displayRecipients[0], currentUser))) {
            recipients = recipients.filter(recipient => !isCurrentUser(recipient, currentUser));
            recipients = [currentUser as DataAlertRecipientInfo].concat(recipients);
          }

          recipients = recipients.slice(0, params.numRecipientsShown);

          const moreCount = dataAlert.recipientsCount - recipients.length || null;
          const localizedCount = L10n.localizedCount(moreCount);
          const moreText = moreCount > 0 && Messages.moreRecipients({ COUNT_LOC: localizedCount });

          const recipientElements = recipients.map((r, i) => {
            const text = getRecipientText(r);
            return (
              <span key={ getRecipientId(r) }>
                { i > 0 ? ', ' : null  }
                <a href={ getRecipientUrl(r) } title={ text }>{ text }</a>
              </span>
            );
          });
          return (
            <CellWithCount testId={`${params.testId}-${this.props.row.data.id}`} count={moreText} onCountClick={this.onClickFn}>
              <span style={ this.textStyles }>{ recipientElements }</span>
            </CellWithCount>
          );
        }
      }
    ));

    function isCurrentUser(recipient: DataAlertRecipientInfo, currentUser: UserDataAlertRecipientInfo): boolean {
      return (DataAlertRecipientInfoType.isUser(recipient)
        && recipient.user.id === currentUser.user.id);
    }

    function getRecipientText(recipient: DataAlertRecipientInfo): string {
      let text = '';
      if (DataAlertRecipientInfoType.isUser(recipient)) {
        text = recipient.user.displayName;
      } else if (DataAlertRecipientInfoType.isGroup(recipient)) {
        text = recipient.group.name;
      }
      return text;
    }

    function getRecipientUrl(recipient: DataAlertRecipientInfo): string {
      let url = '';
      if (DataAlertRecipientInfoType.isUser(recipient)) {
        url = params.siteUrls.userUrl(recipient.user.username, recipient.user.domainName);
      } else if (DataAlertRecipientInfoType.isGroup(recipient)) {
        url = params.siteUrls.groupUrl(recipient.group.id);
      }
      return url;
    }

    function getRecipientId(recipient: DataAlertRecipientInfo): string {
      let id = '';
      if (DataAlertRecipientInfoType.isUser(recipient)) {
        id = recipient.user.id;
      } else if (DataAlertRecipientInfoType.isGroup(recipient)) {
        id = recipient.group.id;
      }
      return id;
    }
  }

  export function getViewName(data: IDataAlertInfo): string {
    return data.context.isViewModified ? data.context.name + '*' : data.context.name;
  }

  export function getViewUrl(data: IDataAlertInfo, siteUrls: SpacesSiteUrls): string {
    if (data.context.isViewModified) {
      const finalSlashIndex = data.context.path.lastIndexOf('/');
      const penultimateSlashIndex = data.context.path.substring(0, finalSlashIndex).lastIndexOf('/');
      const originalViewPath = data.context.path.substring(0, penultimateSlashIndex);
      return siteUrls.viewWithDeviceTypeUrl(originalViewPath, data.deviceType);
    }
    else {
      return siteUrls.viewWithDeviceTypeUrl(data.context.path, data.deviceType);
    }
  }
}
